import { useEffect, useState } from "react";
import {
  ProductDTO,
  ProductDetailDTO,
  Role,
  ServicingTrainerType,
  SubscriptionDTO,
  SwapType,
  UnlistedPriceDTO,
  UpgradeType,
} from "@campgladiator/cg-common.types.types";
import {
  UpgradeSubscriptionArgs,
  upgradeSubscription,
} from "@campgladiator/cg-common.api.subscriptions";
import { getUpgradeableProducts } from "@campgladiator/cg-common.logic.subscriptions";
import { useWithUpgradeMembershipModal } from "../components/UpgradeMembershipModal";
import { fetchTrainerById } from "@campgladiator/cg-common.api.trainers";
import {
  getProperPrice,
  loadUnlistedPrice,
} from "@campgladiator/cg-common.logic.prices";
import { ButtonProps } from "@campgladiator/cgui-core.atoms.button";

type UseUpgradeMembershipProps = {
  areEligibleProductsLoading: boolean;
  genericErrorMessage: string;
  isCouponStepVisible?: boolean;
  onClose: (success?: boolean) => void;
  populateSubscriptionsRows?: (userId: string) => Promise<void>;
  productDetails?: ProductDetailDTO[];
  role: Role;
  setGrowlMessageDescription: (message: string) => void;
  setGrowlMessageVisible: (visible: boolean) => void;
  subscription: SubscriptionDTO;
  theme: ButtonProps["theme"];
  token: string;
  swapType?: boolean;
};

const filterAndMapProductDetails = (
  productDetails: ProductDetailDTO[],
  subscription: SubscriptionDTO
): UnlistedPriceDTO[] => {
  if (productDetails && productDetails[0]) {
    return productDetails[0].products
      .filter(
        (product) =>
          product.termLength > 1 &&
          product.termLength >= subscription.termLength
      )
      .map((product) => {
        const {
          active,
          prices,
          stripeProductName: name,
          stripeProductId,
          termLength,
          type,
          id,
        } = product;

        const price = prices[0];

        if (!price) return null;

        const { amount, currency, stripePriceId, term } = price;

        return {
          id,
          active,
          amount,
          currency,
          name,
          stripePriceId,
          stripeProductId,
          term,
          termLength,
          type,
        };
      });
  }
  return [];
};

const useUpgradeMembership = ({
  areEligibleProductsLoading,
  genericErrorMessage,
  productDetails,
  subscription,
  onClose,
  populateSubscriptionsRows,
  setGrowlMessageDescription,
  setGrowlMessageVisible,
  isCouponStepVisible,
  role,
  token,
  theme,
}: UseUpgradeMembershipProps) => {
  const [isUpgradeLoading, setIsUpgradeLoading] = useState<boolean>(false);
  const [upgradedStripeSubscriptionId, setUpgradedStripeSubscriptionId] =
    useState<SubscriptionDTO["stripeSubscriptionId"]>("");
  const [upgradedSubscriptionId, setUpgradedSubscriptionId] =
    useState<SubscriptionDTO["id"]>("");
  const [selectedMembershipName, setSelectedMembershipName] =
    useState<SubscriptionDTO["productName"]>("");
  const [currentTrainerId, setCurrentTrainerId] =
    useState<ServicingTrainerType["id"]>();
  const [
    isUpgradeSubscriptionModalVisible,
    setIsUpgradeSubscriptionModalVisible,
  ] = useState<boolean>(false);
  const [selectedMembershipPrice, setSelectedMembershipPrice] =
    useState<string>("");
  const [selectedSubscriptionId, setSelectedSubscriptionId] =
    useState<SubscriptionDTO["id"]>("");
  const [selectedSubscriptionProductType, setSelectedSubscriptionProductType] =
    useState<SubscriptionDTO["productType"]>();
  const [
    selectedSubscriptionProductSubType,
    setSelectedSubscriptionProductSubType,
  ] = useState<SubscriptionDTO["productSubType"]>();
  const [currentTrainerPhotoUrl, setCurrentTrainerPhotoUrl] =
    useState<ServicingTrainerType["photoUrl"]>("");
  const [currentTrainerName, setCurrentTrainerName] =
    useState<ServicingTrainerType["name"]>("");
  const [upgradeEligibleProducts, setUpgradeEligibleProducts] = useState<
    ProductDTO[]
  >([]);
  const [upgradedMembershipName, setUpgradedMembershipName] =
    useState<SubscriptionDTO["productName"]>("");
  const [isPriceLoading, setIsPriceLoading] = useState<boolean>(false);

  const handleUpgradeSuccess = (result: SubscriptionDTO) => {
    if (!!result && populateSubscriptionsRows) {
      populateSubscriptionsRows(result.userId);
    }
    return result;
  };

  const handleUpgradeError = (error: any) => {
    setGrowlMessageDescription(genericErrorMessage);
    setIsUpgradeSubscriptionModalVisible(false);
    setGrowlMessageVisible(true);
    return false;
  };

  const handleOnUpgradeSubmit = async (args: UpgradeSubscriptionArgs) => {
    setIsUpgradeLoading(true);
    const upgradePromise = await upgradeSubscription(args)
      .then(handleUpgradeSuccess)
      .catch(handleUpgradeError)
      .finally(() => {
        setIsUpgradeLoading(false);
      });
    const { stripeSubscriptionId, productName, id, servicingTrainer } =
      upgradePromise as SubscriptionDTO;

    setUpgradedStripeSubscriptionId(stripeSubscriptionId);
    setUpgradedSubscriptionId(id);
    setUpgradedMembershipName(productName);
    setCurrentTrainerId(servicingTrainer);
    return !!upgradePromise;
  };

  const getServicingTrainerInfo = async (trainerId: string) => {
    const result = await fetchTrainerById({ trainerId });
    const name = `${result.firstName} ${result.lastName}`;
    const photoUrl = result.photo;
    const servicingTrainerData = { name, photoUrl };
    return servicingTrainerData;
  };

  const handleOnUpgradeSubscriptionStart = async (
    subscription: SubscriptionDTO,
    upgradeType?: UpgradeType,
    swapType?: SwapType
  ) => {
    setIsUpgradeSubscriptionModalVisible(true);
    setIsPriceLoading(true);
    try {
      const { name, photoUrl } = await getServicingTrainerInfo(
        subscription.servicingTrainer
      );
      setCurrentTrainerPhotoUrl(photoUrl);
      setCurrentTrainerName(name);
    } catch (error) {
      setCurrentTrainerName("");
      setCurrentTrainerPhotoUrl("");
    }

    const { id, productType, productSubType, contractStatistics } =
      subscription;
    const { priceSelection } = contractStatistics;

    const recommitProductDetails: UnlistedPriceDTO[] =
      filterAndMapProductDetails(productDetails, subscription);

    try {
      await loadUnlistedPrice(recommitProductDetails);
      const unlistedPrices = getProperPrice(priceSelection, swapType);
      const { upgradeableProducts, productName, productPrice } =
        await getUpgradeableProducts({
          subscription,
          productDetails,
          upgradeType,
          unlistedPrices,
          swapType,
          priceSelection,
          role,
        });
      setSelectedMembershipName(productName);
      setSelectedMembershipPrice(productPrice);
      setSelectedSubscriptionId(id);
      setSelectedSubscriptionProductType(productType);
      setSelectedSubscriptionProductSubType(productSubType);
      setUpgradeEligibleProducts(upgradeType ? upgradeableProducts : []);
      setCurrentTrainerId(subscription?.servicingTrainer);
    } catch (error) {
      onClose(false);
    } finally {
      setIsPriceLoading(false);
    }
  };

  const upgradeMembershipProps = useWithUpgradeMembershipModal({
    areEligibleProductsLoading,
    currentServicingTrainer: currentTrainerId,
    currentTrainerName,
    currentTrainerPhotoUrl,
    isUpgradeLoading: isUpgradeLoading,
    isVisible: isUpgradeSubscriptionModalVisible,
    membership: upgradeEligibleProducts,
    productName: selectedMembershipName,
    productPrice: selectedMembershipPrice,
    stripeSubscriptionId: upgradedStripeSubscriptionId,
    subscriptionId: selectedSubscriptionId,
    subscriptionProductSubType: selectedSubscriptionProductSubType!,
    subscriptionProductType: selectedSubscriptionProductType!,
    upgradedSubscriptionId,
    upgradedSubscriptionProgramName: upgradedMembershipName,
    onClose: (success?: boolean) => {
      setIsUpgradeSubscriptionModalVisible(false);
      onClose(success);
    },
    onSubmitUpgrade: handleOnUpgradeSubmit,
    isCouponStepVisible,
    currentSubscription: subscription,
    role,
    isPriceLoading,
    token,
    theme,
  });

  const { upgradeType, swapType } = upgradeMembershipProps;

  useEffect(() => {
    handleOnUpgradeSubscriptionStart(subscription, upgradeType, swapType);
  }, [subscription, productDetails, upgradeType, swapType]);

  return {
    upgradeMembershipProps,
  };
};

export default useUpgradeMembership;
